import * as React from "react";
import { useMappedState } from "redux-react-hook";
import * as _ from "lodash";

import tokenizeString from "src/library/utils/string-manipulations/tokenize-string";
import type { ContextType } from "src/core/shared/hooks/menu/useMenu";
import type { IRoute } from "src/pages/Router/routes";

import type { IStore } from "../../store";
import useGetMenuItemByPath from "./useGetMenuItemByPath";
import type { MenuContextType } from "./useMenu";

const useContextToMenuContext = () => {
  const { global: { settings, history } } = useMappedState(({ global }: IStore) => ({ global }));
  const getMenuItemByPath = useGetMenuItemByPath();

  const contextToMenuContext = React.useCallback(
    (type: ContextType, currentContext: MenuContextType[], routes: IRoute[]) => {
      let defaultOrigin = null;

      if (currentContext.length) {
        const currentContextType = currentContext[ 0 ].type;

        const originLocationIndex =
          _.findIndex(history, loc => {
            return !loc.state || loc.state.context !== currentContextType;
          }) + 1;

        if (originLocationIndex > 0) {
          const newOrigin = history[ originLocationIndex ];

          if (newOrigin) {
            defaultOrigin = newOrigin.pathname;
          }
        }
      } else if (history && history.length > 1) {
        const route = getMenuItemByPath(history[ 1 ].pathname, routes);

        if (route && route.context !== type) {
          if (!route.allowedContexts || !route.allowedContexts.includes(type)) {
            defaultOrigin = history[ 1 ].pathname;
          }
        }
      } else {
        console.debug("Reseting defaultOrigin due to undefined history");
      }

      // reset to dashboard if needed as a backup
      if (!defaultOrigin) {
        defaultOrigin = "/";
      }

      if (!settings) {
        return null;
      }

      switch (type) {
        case "ORDER":
          return {
            title: tokenizeString("Edit %ORDER%", settings.terminology),
            origin: defaultOrigin || "/products",
            type
          };

        case "CREATE_EDIT_USER":
          return {
            title: tokenizeString("%USER% settings", settings.terminology),
            origin: defaultOrigin || "/admin/users",
            type,
            parent: getMenuItemByPath(defaultOrigin || "/admin/users", routes) || undefined
          };

        case "CREATE_EDIT_USER_GROUP":
          return {
            title: "User group settings",
            origin: "/admin/users/groups/all",
            type,
            parent: getMenuItemByPath(defaultOrigin || "/admin/users", routes) || undefined
          };

        case "CREATE_EDIT_CUSTOMER":
          return {
            title: tokenizeString("%CUSTOMER% settings", settings.terminology),
            origin: defaultOrigin || "/admin/customers",
            type,
            parent: getMenuItemByPath(defaultOrigin || "/admin/customers", routes) || undefined
          };

        case "CREATE_EDIT_DROP":
          return {
            title: tokenizeString("%DROP% settings", settings.terminology),
            origin: defaultOrigin || "/admin/customers",
            type,
            parent: getMenuItemByPath(defaultOrigin || "/admin/customers", routes) || undefined
          };

        case "CREATE_EDIT_CUSTOMER_USER":
          return {
            title: tokenizeString("%USER% settings", settings.terminology),
            origin: defaultOrigin || "/admin/users",
            type,
            parent: getMenuItemByPath(defaultOrigin || "/admin/users", routes) || undefined
          };

        case "LIST_USERS":
          return {
            title: tokenizeString("%USERS%", settings.terminology),
            origin: "/admin/customers",
            type,
            parent: getMenuItemByPath(defaultOrigin || "/admin/customers", routes) || undefined
          };
        case "CREATE_EDIT_PRODUCT":
          return {
            title: tokenizeString("%PRODUCT% settings", settings.terminology),
            origin: defaultOrigin || "/admin/products",
            type,
            parent: getMenuItemByPath(defaultOrigin || "/admin/products", routes) || undefined
          };

        case "CREATE_EDIT_PROMOTION":
          return {
            title: "Edit promotion",
            origin: defaultOrigin || "/admin/promotions",
            type,
            parent: getMenuItemByPath(defaultOrigin || "/admin/promotions", routes) || undefined
          };
        case "CREATE_EDIT_PROMOTIONAL_TAG":
          return {
            title: "Edit promotional tag",
            origin: defaultOrigin || "/admin/promotions/promotional-tags",
            type,
            parent: getMenuItemByPath(defaultOrigin || "/admin/promotions", routes) || undefined
          };

        case "CREATE_EDIT_NOTIFICATION":
          return {
            title: tokenizeString("%NOTIFICATION% settings", settings.terminology),
            origin: defaultOrigin || "/platform/notifications",
            type,
            parent: getMenuItemByPath(defaultOrigin || "/platform/notifications", routes) || undefined
          };
        case "ORDER_PRODUCT_SELECTION":
          return {
            title: tokenizeString("Exit %ORDER%", settings.terminology),
            origin: defaultOrigin || "/orders",
            type,
            parent: getMenuItemByPath(defaultOrigin || "/products", routes) || undefined
          };
        case "LIST_PRODUCT_SELECTION":
          return {
            title: tokenizeString("Exit %LIST%", settings.terminology),
            origin: defaultOrigin || "/lists",
            type,
            parent: getMenuItemByPath(defaultOrigin || "/products", routes) || undefined
          };
        case "PLATFORM_SETTINGS":
          return {
            title: "Platform Settings",
            origin: "/admin/dashboard",
            type,
            parent: getMenuItemByPath(defaultOrigin || "/dashboard", routes) || undefined
          };

        case "CREATE_EDIT_DELIVERY_ROUTE":
          return {
            title: tokenizeString("Delivery route settings", settings.terminology),
            origin: defaultOrigin || "/admin/delivery-routes",
            type,
            parent: getMenuItemByPath(defaultOrigin || "/admin/delivery-routes", routes) || undefined
          };

        case "CREATE_EDIT_PRICE_CODE":
          return {
            title: tokenizeString("Price code Settings", settings.terminology),
            origin: defaultOrigin || "/admin/products/price-lists",
            type,
            parent: getMenuItemByPath(defaultOrigin || "/admin/products/price-lists", routes) || undefined
          };

        default:
          return null;
      }
    },
    [ history, getMenuItemByPath ]
  );

  return contextToMenuContext;
};

export default useContextToMenuContext;
